import React from 'react';

import CodeSample from '../components/CodeSample';

import { OpenOutline } from '@aurora/icons-react';
import {
  H1,
  H2,
  H3,
  H4,
  Preamble,
  Code,
  Paragraph,
  Divider,
  Link,
} from '@aurora/widgets-react';

const DataRequests = () => (
  <React.Fragment>
    <H1>Data Requests</H1>
    <Preamble>
      This page describes how data requests are made to the API.
    </Preamble>
    <Paragraph>
      Data requests are made to the <Code>history</Code> or{' '}
      <Code>forecast</Code> services at urls{' '}
      <Code>https://enlossws.smhi.se/api/history</Code> or{' '}
      <Code>https://enlossws.smhi.se/api/forecast</Code>.
    </Paragraph>
    <Paragraph>
      Requests are specified by providing query parameters. Please see the
      OpenAPI specifications linked from <Link to="/">Home</Link> for exact
      defintions of all query parameters. The sections below describes special
      features of some query parameters.
    </Paragraph>
    <H3>Available data</H3>
    <Paragraph>
      For historic requests there is a limit for how far back you can get data.
      For monthly values there are <b>10 years</b> data available. For daily
      values there are <b>5 years</b> data available and for hourly values you
      can get get data <b>from 2021-01-01</b> at this point. The 5 and 10 years
      is a sliding limit.
    </Paragraph>
    <H2>Query Parameters</H2>
    <H3>placenr</H3>
    <Paragraph>
      Requests are made for 1 place only and only 1 <Code>placenr</Code> can be
      provided. <Code>placenr</Code> is required.
    </Paragraph>
    <Paragraph>
      See{' '}
      <Link to="https://github.com/SMHI-Fastighet/webservice-api-docs/blob/master/ortslista.csv">
        Placenrs
        <OpenOutline />
      </Link>{' '}
      for a list of all places and their placenrs.
    </Paragraph>
    <H3>parameternrs</H3>
    <Paragraph>
      It is possible to request 1 or more <Code>parameternrs</Code>. When
      requesting more than 1 parameternr, separate the parameternrs with a comma
      (,) or specify parameternrs many times with an ampersand (&) between them.{' '}
      <Code>parameternrs</Code> is required.
    </Paragraph>
    <H4>Example</H4>
    <Paragraph>
      <Code>...?parameternrs=5,316</Code> or{' '}
      <Code>...?parameternrs=5&parameternrs=316</Code>
    </Paragraph>
    <Paragraph>
      It is not possible to request many parameternrs with different temporal
      resolution. Our backend consists of hourly, daily and monthly values and
      these cannot be mixed in the same request.
    </Paragraph>
    <H3>typecasenr</H3>
    <Paragraph>
      When requesting parameters that are typecase dependent, a{' '}
      <Code>typecasenr</Code> must be provided. A request can only be made for 1{' '}
      <Code>typecasenr</Code>.
    </Paragraph>
    <Paragraph>
      For monthly Energy Index and Heating Degree Days at subscription level{' '}
      <b>Bas</b> only the standard typecasenr 1024 (EI) and 1117 (GD) are
      available. To be able to use all typecases you need a level <b>Plus</b>{' '}
      subscription. See also <Link to="/products">Products</Link>.
    </Paragraph>
    <H3>timezone</H3>
    <Paragraph>
      Specify which timezone the returned timestamps should be in. Defaults to
      UTC if not specified. Understands most IANA timezones, e.g.
      <Code>Europe/Stockholm</Code>. If timezone is used the timestamps will
      always be local time <b>also using DST</b>. This will have the effect that
      one hour is missing going into DST and it will be an extra hour going back
      to normal time.
    </Paragraph>
    <Paragraph>
      When requesting offsets from UTC, use e.g. <Code>+0100</Code> which URL
      encodes to <Code>...timezone=%2B0100</Code>. A modern HTTP Client can
      probably URL encode this for you. Using offset will not care about DST. It
      will always be UTC+offset.
    </Paragraph>
    <H3>from and to</H3>
    <Paragraph>
      Specify a start date with <Code>from</Code> and an end date with{' '}
      <Code>to</Code>. If <Code>to</Code> is not provided, data will be returned
      from <Code>from</Code> to now. <Code>from</Code> and <Code>to</Code> is
      interpreted to be in the timezone specified by the <Code>timezone</Code>{' '}
      query parameter.
    </Paragraph>
    <Paragraph>
      It is possible to use both <Code>YYYY-mm-dd</Code> or{' '}
      <Code>YYYY-mm-dd HH:MM:SS</Code> syntax. Remember to URL encode the space
      if the time syntax is chosen.
    </Paragraph>

    <Paragraph>
      If <Code>from</Code> and <Code>to</Code> is not given previous 7 days of
      values will be returned for hourly and daily values and the last month for
      monthly values.
    </Paragraph>

    <Paragraph>
      Not available in the <Code>forecast</Code> service.
    </Paragraph>

    <H3>normalperiod</H3>
    <Paragraph>
      When requesting parameters that are dependent on a normal period, e.g.
      normal values, the normal period can be selected with{' '}
      <Code>normalperiod</Code>. Currently only{' '}
      <Code>...normalperiod=1991-2020</Code> is available. Defaults to 1991-2020
      if left out.
    </Paragraph>

    <Paragraph>
      Not available in the <Code>forecast</Code> service.
    </Paragraph>

    <H3>Examples</H3>
    <Paragraph>
      Requesting hourly temperature (parameternr=5) and hourly Cooling Index
      (parameternr=316) in Stockholm (placenr=102612) for typecase 1016 in local
      time (timezone=Europe/Stockholm) between the dates 2021-01-01 and
      2021-01-31 23:00:00 results in the query string
    </Paragraph>

    <Paragraph>
      <CodeSample
        componentString="https://enlossws.smhi.se/api/history?placenr=102612&parameternrs=5,316&typecasenr=1016&timezone=Europe/Stockholm&from=2021-01-01&to=2021-01-31%2023:00:00"
        language="text"
      />
    </Paragraph>

    <Paragraph>
      Specifying parameternrs many times is also valid, e.g.
    </Paragraph>
    <Paragraph>
      <CodeSample
        componentString="https://enlossws.smhi.se/api/history?placenr=102612&parameternrs=5&parameternrs=316&typecasenr=1016&timezone=Europe/Stockholm&from=2021-01-01&to=2021-01-31%2023:00:00"
        language="text"
      />
    </Paragraph>

    <Divider />

    <Paragraph>
      Requesting the last month&apos;s monthly Energy Index, actual
      (parameternr=18331), normal (parameternr=28331), recommended correction
      factor (parameternr=16331) in Norrköping (placenr=102409) for typecase
      1024 results in the query string
    </Paragraph>
    <Paragraph>
      <CodeSample
        componentString="https://enlossws.smhi.se/api/history?placenr=102409&parameternrs=18331,28331,16331&typecasenr=1024"
        language="text"
      />
    </Paragraph>

    <Divider />

    <Paragraph>
      Requesting a forecast of hourly temperature (parameternr=5) for Göteborg
      (placenr=102201) in timezone UTC+1 results in the query string
    </Paragraph>
    <Paragraph>
      <CodeSample
        componentString="https://enlossws.smhi.se/api/forecast?placenr=102409&parameternrs=5&timezone=%2B0100"
        language="text"
      />
    </Paragraph>

    <H2>Response</H2>
    <Paragraph>
      If the request is valid, the API will respond with json in the following
      format
    </Paragraph>

    <CodeSample
      componentString={`{
  "placenr": integer,
  "typecasenr": integer,
  "placename": string,
  "timezone": string,
  "normalperiod": string,
  "issued": datetime string,
  "starttimes": array of datetime strings,
  "<parameternr 0>": array of floats,
  "<parameternr N>": array of floats,
}`}
      language="json"
    />

    <Paragraph>
      Each requested parameternr will be a key in the json body. Its value is an
      array of floats where the indices matches the indices in{' '}
      <Code>starttimes</Code>.
    </Paragraph>

    <Paragraph>
      <Code>starttimes</Code> is exactly what is sounds like. It is an array of
      datetime strings with the starttime of the parameters. For hourly values,{' '}
      <Code>starttimes</Code> should be interpreted as the instant where the
      parameter is valid. For daily and monthly values, <Code>starttimes</Code>{' '}
      is the starting datetime for the aggregation.
    </Paragraph>

    <Paragraph>
      <Code>issued</Code> is a datetime that describes when the forecast was
      issued and will not be included when making requests to the{' '}
      <Code>history</Code> service.
    </Paragraph>

    <H3>Example</H3>
    <Paragraph>
      Given a request for actual daily heating degree values (parameternr=5330)
      and normal daily heating degree values (parameternr=25330) in Malmö
      (placenr=102105) and balancetemperature 17 degrees (typecasenr=1117)
      between the dates 2021-01-01 and 2021-01-05 was made, the API will respond
      with
    </Paragraph>

    <CodeSample
      componentString={`{
  "placenr": 102105,
  "typecasenr": 1117,
  "placename": "Malmö",
  "timezone": "UTC",
  "normalperiod": "1991-2020",
  "starttimes": ["2021-01-01 00:00:00", "2021-01-02 00:00:00", "2021-01-03 00:00:00", "2021-01-04 00:00:00", "2021-01-05 00:00:00"],
  "5330": [12.2, 13.4, 14.5, 16.5, 17.3],
  "25330": [13.3, 13.4, 13.5, 13.5, 13.4]
}`}
      language="json"
    />
  </React.Fragment>
);

export default DataRequests;
